import type {
	FilterRuleModel,
	HTTPValidationError,
	OptimizationModel,
} from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { client } from '@/shared/api/client'
import { clarify } from '@/shared/components/ui/clarify'
import { confirm } from '@/shared/components/ui/confirmer'
import { OptimizationAction, OptimizationStatus } from '@/shared/generated'

import { useOptimizationQuery } from '../api/queries'
import { isEditedFinalFields, isEditedModelFields } from './helpers'

type RequestPayload = {
	filters: FilterRuleModel[]
	values: Record<string, any>
}
export function useUpdateProductsFieldsByFiltersMutation(
	optimizationGroupId: string,
	options?: UseMutationOptions<
		OptimizationModel | null | undefined,
		AxiosError<HTTPValidationError>,
		RequestPayload
	>
) {
	const { t } = useTranslation()

	const { data: optimization } = useOptimizationQuery(optimizationGroupId, {
		enabled: !!optimizationGroupId,
	})

	return useMutation<
		OptimizationModel | null | undefined,
		AxiosError<HTTPValidationError>,
		RequestPayload
	>({
		mutationFn: async (payload: RequestPayload) => {
			const isFinalFieldsChanged = isEditedFinalFields([payload.values])
			const isModelFieldsChanged = isEditedModelFields([payload.values])
			if (
				[OptimizationStatus.Finished, OptimizationStatus.Failed].includes(
					optimization!.status
				)
			) {
				if (isModelFieldsChanged && isFinalFieldsChanged) {
					const saveStartegy = await clarify({
						title: t('products_saving_or_final_title'),
						description: t('products_saving_model_or_final'),
						actions: [
							{ actionId: 'model', actionText: t('general_apply') },
							{
								actionId: 'final',
								actionText: t('apply_only_final_price_changes'),
							},
						],
					})

					if (saveStartegy === 'model') {
						await client.optimizations.optimizationAction({
							action: OptimizationAction.Reset,
							optimizationGroupId,
						})

						return client.optimizations.optimizationProductDiffsFilters(
							{ optimizationGroupId },
							payload
						)
					} else if (saveStartegy === 'final') {
						return client.optimizations.optimizationFinalFieldsFilters(
							{
								optimizationGroupId,
							},
							{
								filters: payload.filters,
								final_fields: payload.values,
							}
						)
					}
				} else if (isModelFieldsChanged) {
					const confirmation = await confirm({
						title: t('products_saving_title'),
						description: t('products_saving_model'),
						actionText: t('general_save_changes'),
						cancelText: t('general_cancel'),
					})
					if (confirmation) {
						await client.optimizations.optimizationAction({
							action: OptimizationAction.Reset,
							optimizationGroupId,
						})

						return client.optimizations.optimizationProductDiffsFilters(
							{ optimizationGroupId },
							payload
						)
					}
				} else {
					return client.optimizations.optimizationFinalFieldsFilters(
						{ optimizationGroupId },
						{
							filters: payload.filters,
							final_fields: payload.values,
						}
					)
				}
			} else {
				return client.optimizations.optimizationProductDiffsFilters(
					{ optimizationGroupId },
					payload
				)
			}
		},
		...(options || {}),
	})
}
